import { useState, useEffect, useRef } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { FigurePanel } from '@/store/figure-composition';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { useFigureCompositionStore } from '@/store/figure-composition';
import { PanelContentTab } from './tabs/PanelContentTab';
import { PanelLabelTab } from './tabs/PanelLabelTab';
import { PanelLegendAxesTab } from './tabs/PanelLegendAxesTab';
import { PanelAnnotationsTab } from './tabs/PanelAnnotationsTab';

interface PanelPropertiesProps {
  panel: FigurePanel;
  showAdvanced: boolean;
  activeTab?: string;
  onTabChange?: (tab: string) => void;
}

export function PanelProperties({ panel, activeTab: controlledTab, onTabChange }: PanelPropertiesProps) {
  const setSelectedPanelId = useFigureCompositionStore((state) => state.setSelectedPanelId);
  const selectedAnnotationId = useFigureCompositionStore((state) => state.selectedAnnotationId);
  const [internalTab, setInternalTab] = useState('content');
  
  // Use controlled tab if provided, otherwise use internal state
  const activeTab = controlledTab !== undefined ? controlledTab : internalTab;
  const setActiveTab = onTabChange || setInternalTab;

  // Track previous annotation ID to detect when it's closed
  const prevAnnotationIdRef = useRef<string | null>(null);

  // Switch to annotations tab when an annotation is selected
  useEffect(() => {
    if (selectedAnnotationId) {
      setActiveTab('annotations');
      prevAnnotationIdRef.current = selectedAnnotationId;
    } else if (prevAnnotationIdRef.current !== null && !selectedAnnotationId) {
      // Annotation was closed - switch back to annotations tab
      setActiveTab('annotations');
      prevAnnotationIdRef.current = null;
    }
  }, [selectedAnnotationId, setActiveTab]);

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b flex-shrink-0">
        <div>
          <div className="text-sm font-semibold">Panel {panel.label}</div>
          <div className="text-xs text-muted-foreground">{panel.plotId ? 'Plot' : 'Empty'}</div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => setSelectedPanelId(null)}
          title="Deselect panel"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Horizontal tabs */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col h-full">
          {/* Horizontal tabs on top */}
          <div className="border-b border-border bg-muted/30">
            <div className="overflow-x-auto">
              <TabsList className="flex h-9 min-w-max gap-1 bg-transparent px-3 pt-2 pb-1">
                <TabsTrigger 
                  value="content" 
                  className="h-8 rounded-full px-3 text-xs font-medium data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:text-foreground data-[state=active]:font-semibold data-[state=inactive]:text-muted-foreground hover:bg-muted"
                >
                  Content
                </TabsTrigger>
                <TabsTrigger 
                  value="label" 
                  className="h-8 rounded-full px-3 text-xs font-medium data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:text-foreground data-[state=active]:font-semibold data-[state=inactive]:text-muted-foreground hover:bg-muted"
                >
                  Label
                </TabsTrigger>
                <TabsTrigger 
                  value="legendAxes" 
                  className="h-8 rounded-full px-3 text-xs font-medium data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:text-foreground data-[state=active]:font-semibold data-[state=inactive]:text-muted-foreground hover:bg-muted"
                >
                  Zoom
                </TabsTrigger>
                <TabsTrigger 
                  value="annotations" 
                  className="h-8 rounded-full px-3 text-xs font-medium data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:text-foreground data-[state=active]:font-semibold data-[state=inactive]:text-muted-foreground hover:bg-muted"
                >
                  Annotations
                </TabsTrigger>
              </TabsList>
            </div>
          </div>

          {/* Content area below tabs */}
          <div className="flex-1 overflow-hidden">
            <div className="h-full overflow-y-auto px-3 py-3">
              <TabsContent value="content" className="mt-0">
                <PanelContentTab panel={panel} />
              </TabsContent>
              <TabsContent value="label" className="mt-0">
                <PanelLabelTab panel={panel} />
              </TabsContent>
              <TabsContent value="legendAxes" className="mt-0">
                <PanelLegendAxesTab panel={panel} />
              </TabsContent>
              <TabsContent value="annotations" className="mt-0">
                <PanelAnnotationsTab panel={panel} />
              </TabsContent>
            </div>
          </div>
        </Tabs>
      </div>
    </div>
  );
}
